import { Col, Row, Space } from 'antd';
import { get, map, merge } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';

import PayrollFileConfigurationAPI from '../../../api/payroll-file-configurations';
import { ConfirmModal, CustomTable } from '../../../components';
import LinkText from '../../../components/Text/LinkText';
import Toast from '../../../components/Toast';
import { INTERNAL_LINKS } from '../../../enum';
import usePaginatedFiltersQuery from '../../../hooks/queries/usePaginatedFiltersQuery';
import useModal from '../../../hooks/useModal';
import useTableExpandedRows from '../../../hooks/useTableExpandedRows';
import PayrollExpandedRow from './PayrollExpandedRow';

const PayrollFileConfigurationSection = props => {
  const { t, currentCompany } = props;

  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal, modalProps] = useModal();

  const { expandedRowKeys, toggleExpandedRow } = useTableExpandedRows();

  const setExpandableOnDocuments = useCallback(data => {
    return merge({}, data, {
      documents: map(get(data, 'documents'), document => merge({}, document, { expandable: true })),
    });
  }, []);

  const {
    query: payrollFileConfigurationQuery,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery({
    enabled: !!currentCompany?._id,
    select: setExpandableOnDocuments,
    queryKey: ['fetchPayrollFileConfiguration', currentCompany?._id],
    queryFn: () =>
      new PayrollFileConfigurationAPI().fetchPayrollFileConfiguration(
        { companyId: currentCompany._id },
        paginationConfig.current,
        paginationConfig.pageSize,
      ),
  });

  const deletePayrollFileConfigurationMutation = useMutation(
    payrollId => new PayrollFileConfigurationAPI().deletePayrollFileConfiguration(payrollId),
    {
      onSuccess: () => {
        payrollFileConfigurationQuery.remove();

        Toast({
          type: 'open',
          message: t('deletePayrollFileConfigurationSuccess'),
        });
      },
      onError: () => {
        Toast({
          type: 'open',
          message: t('deletePayrollFileConfigurationError'),
        });
      },
      onSettled: () => {
        closeDeleteModal();
      },
    },
  );

  const COLUMNS = useMemo(
    () => [
      {
        fixed: 'left',
        dataIndex: 'name',
        title: t('fileName'),
      },
      {
        width: 150,
        key: 'actions',
        title: t('actions'),
        render: data => (
          <Row gutter={16}>
            <Col>
              <Link to={INTERNAL_LINKS.UPDATE_CUSTOM_PAYROLL_FILE(data._id, currentCompany._id)}>
                <LinkText>{t('Edit')}</LinkText>
              </Link>
            </Col>
            <Col>
              <LinkText
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDeleteModal({ name: data.name, payrollId: data._id });
                }}
              >
                {t('Delete')}
              </LinkText>
            </Col>
          </Row>
        ),
      },
    ],
    [t, currentCompany, openDeleteModal],
  );

  return (
    <section>
      <Space direction="vertical" size="middle">
        <CustomTable
          withExpandableRows
          showSearchInput={false}
          expandedRowKeys={expandedRowKeys}
          loading={payrollFileConfigurationQuery.isFetching}
          onExpand={(expanded, record) => toggleExpandedRow(record)}
          columns={COLUMNS}
          dataSource={get(payrollFileConfigurationQuery.data, 'documents', [])}
          pagination={{
            pageSize: paginationConfig.pageSize,
            total: paginationConfig.total,
            current: paginationConfig.current,
            onShowSizeChange: handlePageSizeChange,
          }}
          onChange={({ current }, filters, sorters) => {
            handlePageChange(current);
          }}
          expandedRowRender={rowData => <PayrollExpandedRow t={t} {...rowData} />}
          expandedRowStyle={{ padding: 20, background: '#eeeff19e' }}
          scroll={{ x: true }}
        />

        {!payrollFileConfigurationQuery.isFetching &&
          get(payrollFileConfigurationQuery.data, 'documents', []).length === 0 && (
            <Row justify="end">
              <Col>
                <Link to={INTERNAL_LINKS.CREATE_CUSTOM_PAYROLL_FILE(currentCompany._id)}>
                  <LinkText>{t('Add new')}</LinkText>
                </Link>
              </Col>
            </Row>
          )}
      </Space>

      <ConfirmModal
        loading={deletePayrollFileConfigurationMutation.isLoading}
        visible={isDeleteModalOpen}
        title={modalProps.name}
        message={t('sureYouWantToDeleteThisPayrollFile')}
        onCancel={closeDeleteModal}
        okText={t('Delete')}
        onOk={() => deletePayrollFileConfigurationMutation.mutate(modalProps.payrollId)}
      />
    </section>
  );
};

export default PayrollFileConfigurationSection;
